"use client";

/**
 * Переключатель магазина в сайдбаре.
 *
 * Закрытое состояние: иконка + имя текущего магазина + ▾ (либо «Все магазины»
 * если mode='all'). Dropdown — список магазинов текущего аккаунта с ✓ на
 * активном, пункт «Все магазины», разделитель, «+ Новый магазин», «Настройки
 * магазинов». При наведении на переключатель открывает dropdown, при клике на
 * магазин — вызывает switchStore, dropdown закрывается.
 *
 * Если у юзера один магазин в единственном аккаунте — dropdown всё равно
 * доступен (для кнопок «Добавить магазин» и «Настройки»), но пункт «Все» не
 * показываем (не имеет смысла когда магазин один).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { Check, ChevronDown, ChevronsUpDown, Plus, Settings, Store } from "lucide-react";
import { useStore } from "@/app/context/StoreContext";
import { useExitAnimation } from "@/app/hooks/useExitAnimation";

function StoreSwitcherIcon({ compact = false }: { compact?: boolean }) {
  return (
    <span className="relative flex h-5 w-5 items-center justify-center">
      <Store size={compact ? 16 : 15} strokeWidth={1.9} />
      {compact && (
        <ChevronDown className="absolute -bottom-0.5 -right-1 text-current opacity-70" size={9} strokeWidth={2.2} />
      )}
    </span>
  );
}

export default function StoreSwitcher({ compact = false }: { compact?: boolean }) {
  const { store, stores, account, mode, loading, switchStore } = useStore();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [compactMenuPos, setCompactMenuPos] = useState<{ left: number; bottom: number } | null>(null);
  const [compactTip, setCompactTip] = useState<{ label: string; top: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const menu = useExitAnimation(open);

  const cancelCloseTimer = useCallback(() => {
    if (closeTimerRef.current === null) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  const closeWithDelay = useCallback(() => {
    cancelCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      setHovered(false);
      setCompactTip(null);
      closeTimerRef.current = null;
    }, 140);
  }, [cancelCloseTimer]);

  const updateCompactMenuPos = useCallback(() => {
    if (!compact || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setCompactMenuPos({
      left: rect.right + 6,
      bottom: Math.max(8, window.innerHeight - rect.bottom),
    });
  }, [compact]);

  const showCompactTip = (label: string, element: HTMLElement) => {
    if (!compact || open) return;
    const rect = element.getBoundingClientRect();
    setCompactTip({ label, top: rect.top + rect.height / 2 });
  };

  const openMenu = useCallback(() => {
    cancelCloseTimer();
    setCompactTip(null);
    updateCompactMenuPos();
    setOpen(true);
  }, [cancelCloseTimer, updateCompactMenuPos]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !compact) return;
    updateCompactMenuPos();
    window.addEventListener("resize", updateCompactMenuPos);
    window.addEventListener("scroll", updateCompactMenuPos, true);
    return () => {
      window.removeEventListener("resize", updateCompactMenuPos);
      window.removeEventListener("scroll", updateCompactMenuPos, true);
    };
  }, [compact, open, updateCompactMenuPos]);

  useEffect(() => {
    setOpen(false);
    setCompactTip(null);
    setCompactMenuPos(null);
  }, [compact]);

  useEffect(() => {
    return () => cancelCloseTimer();
  }, [cancelCloseTimer]);

  if (loading || !account) {
    return (
      <div className={compact ? "relative my-2 flex justify-center" : "relative mx-2 my-2"}>
        <Link
          href="/settings"
          prefetch={false}
          className={compact ? "flex h-8 w-8 items-center justify-center rounded-lg text-sm" : "w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm"}
          style={{
            color: "var(--sidebar-text)",
            border: "1px solid var(--sidebar-border)",
          }}
          title={compact ? undefined : "Настройки"}
          aria-label="Настройки"
          onMouseEnter={e => showCompactTip("Настройки", e.currentTarget)}
          onMouseLeave={() => setCompactTip(null)}
        >
          <span className="text-base">⚙️</span>
          {!compact && <span className="flex-1 truncate text-left font-medium">Настройки</span>}
        </Link>
        {compactTip && (
          <div
            className="pointer-events-none fixed left-[54px] z-[9999] whitespace-nowrap rounded-md border bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-lg"
            style={{ top: compactTip.top, transform: "translateY(-50%)", borderColor: "var(--sidebar-border)" }}
          >
            {compactTip.label}
          </div>
        )}
      </div>
    );
  }

  const label = mode === "all" ? "Все магазины" : (store?.name ?? "—");

  const doSwitch = async (id: string | null) => {
    setOpen(false);
    if (busy) return;
    setBusy(true);
    try { await switchStore(id); } catch (e) { console.error(e); }
    finally { setBusy(false); }
  };

  const dropdown = menu.mounted ? (
    <div
      ref={menuRef}
      onMouseEnter={cancelCloseTimer}
      onMouseLeave={closeWithDelay}
      className={`ui-popover-panel ui-popover-panel-left ui-dropdown-panel overflow-hidden py-0.5 ${compact ? "fixed z-[9999] w-40" : "absolute bottom-full left-0 z-50 mb-1 w-40 max-w-[calc(100vw-24px)]"}`}
      data-state={menu.state}
      style={{
        background: "var(--sidebar-bg)",
        border: "1px solid var(--sidebar-border)",
        ...(compact ? { left: compactMenuPos?.left ?? 56, bottom: compactMenuPos?.bottom ?? 8 } : {}),
      }}
      aria-label={`Магазины аккаунта ${account.name}`}
    >
      <div
        className="overflow-y-auto"
        style={{ maxHeight: compact ? "min(34vh, 190px)" : "min(36vh, 200px)" }}
      >
        {stores.map((s, index) => {
          const active = mode === "single" && store?.id === s.id;
          return (
            <button
              key={s.id}
              onClick={() => doSwitch(s.id)}
              className="ui-appear ui-dropdown-item h-[26px] gap-1 px-1.5 py-0 text-xs hover:bg-black/5"
              style={{
                color: active ? "var(--sidebar-text-active)" : "var(--sidebar-text)",
                animationDelay: `${index * 18}ms`,
              }}
            >
              <span className="flex w-3.5 shrink-0 items-center justify-center">
                {active ? <Check size={12} strokeWidth={2.2} /> : null}
              </span>
              <span className="flex-1 text-left truncate">{s.name}</span>
            </button>
          );
        })}

        {stores.length > 1 && (
          <button
            onClick={() => doSwitch(null)}
            className="ui-dropdown-item h-[26px] gap-1 px-1.5 py-0 text-xs hover:bg-black/5"
            style={{ color: mode === "all" ? "var(--sidebar-text-active)" : "var(--sidebar-text)" }}
          >
            <span className="flex w-3.5 shrink-0 items-center justify-center">
              {mode === "all" ? <Check size={12} strokeWidth={2.2} /> : null}
            </span>
            <span className="flex-1 text-left italic">Все магазины</span>
          </button>
        )}
      </div>

      <div className="ui-dropdown-divider my-0.5 mx-1.5" style={{ background: "var(--sidebar-border)" }} />

      <Link
        href="/settings"
        prefetch={false}
        onClick={() => setOpen(false)}
        className="ui-dropdown-item h-[26px] gap-1 px-1.5 py-0 text-xs hover:bg-black/5"
        style={{ color: "var(--sidebar-text)" }}
      >
        <span className="flex w-3.5 shrink-0 items-center justify-center">
          <Plus size={12} strokeWidth={2.2} />
        </span>
        <span className="flex-1 text-left">Новый магазин</span>
      </Link>
      <Link
        href="/settings"
        prefetch={false}
        onClick={() => setOpen(false)}
        className="ui-dropdown-item h-[26px] gap-1 px-1.5 py-0 text-xs hover:bg-black/5"
        style={{ color: "var(--sidebar-text)" }}
      >
        <span className="flex w-3.5 shrink-0 items-center justify-center">
          <Settings size={12} strokeWidth={2.1} />
        </span>
        <span className="flex-1 text-left">Настройки</span>
      </Link>
    </div>
  ) : null;
  const compactTooltip = compactTip ? (
    <div
      className="pointer-events-none fixed left-[54px] z-[9999] whitespace-nowrap rounded-md border bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-lg"
      style={{ top: compactTip.top, transform: "translateY(-50%)", borderColor: "var(--sidebar-border)" }}
    >
      {compactTip.label}
    </div>
  ) : null;

  return (
    <div
      ref={ref}
      className={compact ? "relative my-2 flex justify-center" : "relative mx-2 my-2"}
      onMouseEnter={cancelCloseTimer}
      onMouseLeave={closeWithDelay}
    >
      <button
        ref={buttonRef}
        onClick={openMenu}
        onMouseEnter={() => {
          setHovered(true);
          openMenu();
        }}
        onMouseLeave={() => {
          setHovered(false);
          if (!open) setCompactTip(null);
        }}
        disabled={busy}
        className={compact
          ? "flex h-8 w-8 items-center justify-center rounded-lg text-sm disabled:opacity-60"
          : "w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm disabled:opacity-60"}
        style={{
          background: open ? "var(--sidebar-hover)" : hovered ? "#eceae4" : "transparent",
          color: "var(--sidebar-text)",
          border: "1px solid var(--sidebar-border)",
          transition: "background-color 0.3s ease",
        }}
        title={compact ? undefined : "Переключить магазин"}
        aria-label="Переключить магазин"
        aria-expanded={open}
      >
        <StoreSwitcherIcon compact={compact} />
        {!compact && (
          <>
            <span className="flex-1 truncate text-left font-medium">{label}</span>
            <ChevronsUpDown size={14} strokeWidth={1.9} className="shrink-0 opacity-60" />
          </>
        )}
      </button>

      {compact && dropdown ? createPortal(dropdown, document.body) : dropdown}
      {compactTooltip && typeof document !== "undefined" ? createPortal(compactTooltip, document.body) : compactTooltip}
    </div>
  );
}
